import {Component} from 'panel';

import {registerMPElement} from 'common/util/register-element';

import template from './index.jade';
import css from './index.styl?inline';

class ReportTitle extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'can-save': `boolean`,
      disabled: `boolean`,
      'has-custom-alerts': {type: `boolean`, default: false},
      'has-dashboards': {type: `boolean`, default: false},
      'has-unsaved-changes': {type: `boolean`, default: false},
      'report-title': `string`,
      'show-save-new': `boolean`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        active: false,
        enabled: true,
        inputValue: ``,
        saveConfirmOpen: false,
        saveFocused: false,
        saveNewFocused: false,
      },
      helpers: {
        onInputInsert: (vnode) => (this.inputEl = vnode.elm),
        blur: () => !this.state.saveConfirmOpen && this.inactivate(),
        focus: () =>
          this.update({
            active: true,
          }),
        inputChange: () => {
          this.update({
            inputValue: this.value,
            saveFocused: false,
            saveNewFocused: false,
          });
        },
        keydownHandler: (ev) => {
          const saveFocused = this.state.saveFocused;
          switch (ev.keyCode) {
            case 9: {
              ev.preventDefault();
              this.update({saveFocused: !saveFocused, saveNewFocused: saveFocused});
              break;
            }
            case 13: {
              const saveNewFocused = this.state.saveNewFocused;
              if (saveFocused || saveNewFocused) {
                this.saveReport(ev, {saveAsNew: saveNewFocused});
              }
            }
          }
        },
        buttonMousedown: (ev) => {
          if (this.state.active) {
            // don't let input blur until we save!
            ev.preventDefault();
          }
        },
        clickSave: (ev) => !this.helpers.isSaveDisabled() && this.saveReport(ev),
        clickSaveNew: (ev) => !this.helpers.isSaveNewDisabled() && this.saveReport(ev, {saveAsNew: true}),
        isSaveDisabled: () => !this.attr(`can-save`) || !this.state.inputValue || !this.isDirty(),
        isSaveNewDisabled: () => !this.attr(`can-save`) || !this.state.inputValue,
        cancelSaveReport: () => this.inactivate({saveConfirmOpen: false}),
        confirmSaveReport: () => {
          this.inactivate({saveConfirmOpen: false});
          this.dispatchChange({save: true, saveAsNew: false});
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.closeOnEscape = (ev) => ev.keyCode === 27 && this.inputEl.blur();
    document.body.addEventListener(`keydown`, this.closeOnEscape);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.body.removeEventListener(`keydown`, this.closeOnEscape);
  }

  getInputValue() {
    return this.state.inputValue;
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    switch (attr) {
      case `disabled`:
        this.update({enabled: !this.attr(`disabled`)});
        break;
      case `report-title`:
        this.update({inputValue: newVal});
        break;
      default:
        break;
    }
  }

  dispatchChange(options = {}) {
    this.dispatchEvent(
      new CustomEvent(`change`, {
        detail: Object.assign({value: this.value}, options),
      }),
    );
  }

  inactivate(extraParams = {}) {
    this.update({
      active: false,
      saveFocused: false,
      saveNewFocused: false,
      ...extraParams,
    });
  }

  isDirty() {
    return this.attr(`has-unsaved-changes`) || this.state.inputValue !== this.attr(`report-title`);
  }

  saveReport(ev, {saveAsNew = false} = {}) {
    ev.stopPropagation();
    if (saveAsNew || (!this.attr(`has-dashboards`) && !this.attr(`has-custom-alerts`))) {
      this.dispatchChange({save: true, saveAsNew});
      this.inputEl.blur();
    } else {
      this.update({saveConfirmOpen: true});
    }
  }

  get value() {
    const inputEl = this.inputEl;
    return inputEl ? inputEl.value : ``;
  }
}

export default registerMPElement(`mp-report-title`, ReportTitle);
